import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { resources } from "@constants/hardcoded-strings";
import { RELYING_PARTIES_DETAIL_URL } from "@constants/routes";
import { RELYING_PARTY_ID_PATTERN } from "@constants/url-parameter-patterns";
import { CellDropdownItem } from "@idw-types/common";
import { navigateToUtil } from "@utils/navigation-util";
import { injectCustomIdParameter } from "@utils/parameter-injector";
import { TableCellWithDropdown } from "@containers/common/table-cell-with-dropdown/table-cell-with-dropdown";
import { RelyingPartiesTableDataRow } from "../relying-parties-table-container";
import { useModalContext } from "@context/use-modal-context";
import { ModalTypes } from "@enums/modal-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import styles from "./relying-party-table-cell.less";
import { EntityStatusUpdateModal } from "@containers/modals/status-account-update-modal/entity-status-update-modal";
import { GenericAccountStateTypes } from "@enums/generic-account-state-types";
import { deleteRelyingPartyReduxRequest } from "@redux-requests/relying-parties/delete-relying-party";
import { useDispatch } from "react-redux";
import { restoreRelyingPartyReduxRequest } from "@redux-requests/relying-parties/restore-relying-party";
import { archiveRelyingPartyReduxRequest } from "@redux-requests/relying-parties/archive-relying-party";
import { entityStatusUpdateModalOptions } from "@constants/entity-modal-options";

type RelyingPartyTableCellProps = {
    data: RelyingPartiesTableDataRow;
    canRestoreDueToLimits: boolean;
};

export const RelyingPartyTableCell = (props: RelyingPartyTableCellProps) => {
    const {
        data,
        canRestoreDueToLimits
    } = props;

    const modalContext = useModalContext();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const dispatch = useDispatch();

    const onClickArchiveRelyingParty = () => {
        const renderArchiveRelyingPartyModal = (onModalClose: () => void) => {

            const handleArchiveRelyingParty = async () => {
                const response = await archiveRelyingPartyReduxRequest(dispatch)(data.id);
                return { data: response };
            }

            return (
                <EntityStatusUpdateModal
                    entityModalOptions={entityStatusUpdateModalOptions['relyingParty']}
                    entityAccountStateType={GenericAccountStateTypes.ARCHIVE}
                    onUpdateStatus={handleArchiveRelyingParty}
                    onModalClose={onModalClose}
                />);
        };

        modalContext.setModalState({
            modalType: ModalTypes.ArchiveRelyingParty,
            renderFunction: renderArchiveRelyingPartyModal
        });
    };

    const onClickRestoreRelyingParty = () => {
        const renderRestoreRelyingPartyModal = (onModalClose: () => void) => {

            const handleRestoreRelyingParty = async () => {
                const response = await restoreRelyingPartyReduxRequest(dispatch)(data.id);
                return { data: response };
            }

            return (
                <EntityStatusUpdateModal
                    entityModalOptions={entityStatusUpdateModalOptions['relyingParty']}
                    entityAccountStateType={GenericAccountStateTypes.RESTORE}
                    onUpdateStatus={handleRestoreRelyingParty}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.RestoreRelyingParty,
            renderFunction: renderRestoreRelyingPartyModal
        });
    };


    const onClickDeleteRelyingParty = () => {
        const renderDeleteRelyingPartyModal = (onModalClose: () => void) => {

            const handleDeleteRelyingParty = async () => {
                const response = await deleteRelyingPartyReduxRequest(dispatch)(data.id);
                return { data: response };
            }

            return (
                <EntityStatusUpdateModal
                    entityModalOptions={entityStatusUpdateModalOptions['relyingParty']}
                    entityAccountStateType={GenericAccountStateTypes.DELETE}
                    onUpdateStatus={handleDeleteRelyingParty}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.DeleteRelyingParty,
            renderFunction: renderDeleteRelyingPartyModal
        });
    };

    const handleOpenRelyingPartyDetails = () => {
        const url = injectCustomIdParameter(
            RELYING_PARTIES_DETAIL_URL,
            data.id,
            RELYING_PARTY_ID_PATTERN
        );
        navigateToUtil(url, navigate, routerLocation.pathname);
    }

    const relyingPartyDropdownItems = useMemo(() => {
        const items: Array<CellDropdownItem> = [];
        const {
            status,
            permissions
        } = data;

        const prefix = "relying-party-list";


        if (permissions.canView) {
            items.push({
                idSuffix: `${prefix}-open`,
                title: resources.tables.relyingParties.dropdownItems.open.label,
                text: resources.tables.relyingParties.dropdownItems.open.label,
                onClick: handleOpenRelyingPartyDetails
            });
        }

        if (status === EntityStatusTypes.ACTIVE) {
            if (permissions.canArchive) {
                items.push({
                    idSuffix: `${prefix}-archive-relying-party`,
                    title: resources.tables.relyingParties.dropdownItems.archiveRelyingParty.label,
                    text: resources.tables.relyingParties.dropdownItems.archiveRelyingParty.label,
                    className: styles.archiveButton,
                    onClick: onClickArchiveRelyingParty
                });
            }
        } else {
            if (permissions.canRestore) {
                items.push({
                    idSuffix: `${prefix}-restore-relying-party`,
                    title: resources.tables.relyingParties.dropdownItems.restoreRelyingParty.label,
                    text: resources.tables.relyingParties.dropdownItems.restoreRelyingParty.label,
                    disabled: !canRestoreDueToLimits,
                    disabledText: resources.tables.relyingParties.dropdownItems.restoreRelyingParty.disabledText,
                    onClick: onClickRestoreRelyingParty
                });
            }
        }

        if (permissions.canDelete) {
            items.push({
                idSuffix: `${prefix}-delete-relying-party`,
                title: resources.tables.relyingParties.dropdownItems.deleteRelyingParty.label,
                text: resources.tables.relyingParties.dropdownItems.deleteRelyingParty.label,
                className: styles.archiveButton,
                onClick: onClickDeleteRelyingParty
            });
        }

        return items;
    }, [
        data,
        canRestoreDueToLimits
    ]);

    return (
        <TableCellWithDropdown
            value={data.name}
            dropdownText={""}
            items={relyingPartyDropdownItems}
            uniqueId={data.id}
        />
    );
};